import Chip from "@mui/material/Chip";
import { Stack, Typography } from "@mui/material";
import { CustomCellDate, CrudLink } from "@/shared/components";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import { PagePaths } from "@/admin/shared/constants";
import { typeLabel, statusLabel, statusChipColor } from "./labels";
import type { SupportRequestRow } from "./types";

export function renderSupportRequestStatus(status: string) {
  return (
    <Chip
      label={statusLabel[status] ?? status}
      color={statusChipColor[status] ?? "default"}
      size="small"
    />
  );
}

function renderUser(row: SupportRequestRow) {
  if (!row.user) return "-";
  return (
    <CrudLink path={PagePaths.users} tab="details" id={row.user.id}>
      {row.user.displayName || row.user.email || row.user.id}
    </CrudLink>
  );
}

export const getColumnsConfig = (): ColumnDefinition<SupportRequestRow>[] => [
  { name: "id", label: "ID", options: { display: false, filter: false } },
  {
    name: "organization",
    label: "Организация",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const row = data as SupportRequestRow;
        if (!row.organization) return "-";
        return (
          <CrudLink
            path={PagePaths.organizations}
            tab="details"
            id={row.organization.id}
          >
            {row.organization.title}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "platform",
    label: "Арена",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const row = data as SupportRequestRow;
        if (!row.platform) return "-";
        return (
          <CrudLink
            path={PagePaths.platforms}
            tab="details"
            id={row.platform.id}
          >
            {row.platform.title}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "type",
    label: "Тип",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const row = data as SupportRequestRow;
        return (
          <CrudLink id={row.id} tab="details">
            {typeLabel[row.type] ?? row.type}
          </CrudLink>
        );
      },
    },
  },
  {
    name: "user",
    label: "Зритель",
    options: {
      sort: false,
      customBodyRender: (data: unknown) =>
        renderUser(data as SupportRequestRow),
    },
  },
  {
    name: "status",
    label: "Статус",
    options: {
      customBodyRender: (value: unknown) =>
        renderSupportRequestStatus(value as string),
    },
  },
  {
    name: "createdAt",
    label: "Создано",
    options: {
      setCellHeaderProps: () => ({ style: { width: 150 } }),
      customBodyRender: (value: unknown) => (
        <CustomCellDate date={value as string} />
      ),
    },
  },
];

export const getTextsConfig = () => ({
  filterTitle: "Фильтры",
  listTitle: "Обращения",
  refetchList: "Обновить список",
  showTitle: "Обращение",
  editItem: "Обращение",
});

export const getMobileColumnsConfig =
  (): DataListViewColumns<SupportRequestRow> => ({
    main: {
      title: "Обращение",
      sx: { flex: 1, minWidth: 0 },
      renderCell: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <CrudLink id={row.id} tab="details" noWrap>
            {typeLabel[row.type] ?? row.type}
          </CrudLink>
          <Typography variant="body2" color="textSecondary" noWrap>
            {row.organization?.title ?? "-"}
          </Typography>
        </Stack>
      ),
    },
    status: {
      title: "Статус",
      sx: { width: 132, flexShrink: 0 },
      renderCell: (row) => renderSupportRequestStatus(row.status),
    },
  });
